/**
 * Move Questions Generator
 * Generates educational questions about chess moves
 */

/**
 * Parse move notation to extract move details
 * @param {string} move - Move in algebraic notation
 * @returns {Object} Parsed move details
 */
function parseMove(move) {
  const castlingKingside = move === 'O-O';
  const castlingQueenside = move === 'O-O-O';
  const isCheck = move.includes('+');
  const isCheckmate = move.includes('#');
  const isCapture = move.includes('x');
  const isPromotion = move.includes('=');
  
  let piece = 'pawn';
  let targetSquare = '';
  let promotionPiece = '';
  
  if (castlingKingside || castlingQueenside) {
    return { 
      piece: 'king', 
      castling: true, 
      castlingType: castlingKingside ? 'kingside' : 'queenside',
      isCheck,
      isCheckmate
    };
  }
  
  // Remove check/checkmate symbols for parsing
  let cleanMove = move.replace(/[+#]/g, '');
  
  // Handle promotion
  if (isPromotion) {
    promotionPiece = cleanMove.split('=')[1];
    cleanMove = cleanMove.split('=')[0];
  }
  
  // Extract piece type
  if (/^[KQRBN]/.test(cleanMove)) {
    const pieceMap = {
      'K': 'king',
      'Q': 'queen',
      'R': 'rook',
      'B': 'bishop',
      'N': 'knight'
    };
    piece = pieceMap[cleanMove[0]];
    cleanMove = cleanMove.slice(1);
  }
  
  // Extract target square (last 2 characters)
  targetSquare = cleanMove.slice(-2);
  
  return {
    piece,
    targetSquare,
    isCapture,
    isCheck,
    isCheckmate,
    isPromotion,
    promotionPiece
  };
}

/**
 * Generate a question about a chess move
 * @param {string} move - The move in algebraic notation
 * @param {Chess} game - Current game state
 * @returns {Object} Question object with text and type
 */
export function generateMoveQuestion(move, game) {
  const parsed = parseMove(move);
  const questions = [];
  
  // Piece-based questions
  if (parsed.piece === 'pawn') {
    questions.push({
      text: "Which pawn needs to move?",
      type: "piece"
    });
    
    if (parsed.isCapture) {
      questions.push({
        text: "Which pawn captures on " + parsed.targetSquare + "?",
        type: "capture"
      });
    } else {
      questions.push({
        text: "Which pawn advances in this position?",
        type: "advance"
      });
    }
  } else if (parsed.piece === 'knight') {
    questions.push({
      text: "Where does the knight go?",
      type: "destination"
    });
    questions.push({
      text: "Which knight moves to " + parsed.targetSquare + "?",
      type: "piece"
    });
  } else if (parsed.piece === 'bishop') {
    questions.push({
      text: "What's the bishop's target square?",
      type: "destination"
    });
    questions.push({
      text: "Which diagonal does the bishop use?",
      type: "pattern"
    });
  } else if (parsed.piece === 'rook') {
    questions.push({
      text: "Where should the rook move?",
      type: "destination"
    });
    questions.push({
      text: "Which file or rank does the rook use?",
      type: "pattern"
    });
  } else if (parsed.piece === 'queen') {
    questions.push({
      text: "What's the queen's destination?",
      type: "destination"
    });
    questions.push({
      text: "Where does the most powerful piece go?",
      type: "destination"
    });
  }
  
  // Special move questions
  if (parsed.castling) {
    questions.push({
      text: "Which castling move is needed here?",
      type: "castling"
    });
    questions.push({
      text: "Time to castle - which side?",
      type: "castling"
    });
  }
  
  if (parsed.isCapture) {
    questions.push({
      text: "What piece captures on " + parsed.targetSquare + "?",
      type: "capture"
    });
    questions.push({
      text: "There's a capture - can you find it?",
      type: "capture"
    });
  }
  
  if (parsed.isCheck) {
    questions.push({
      text: "How do you give check in this position?",
      type: "check"
    });
    questions.push({
      text: "Find the checking move!",
      type: "check"
    });
  }
  
  if (parsed.isPromotion) {
    questions.push({
      text: "A pawn promotes! Where and to what?",
      type: "promotion"
    });
  }
  
  // General questions
  questions.push({
    text: "What's the best move here?",
    type: "general"
  });
  questions.push({
    text: "Find the theoretical move",
    type: "general"
  });
  questions.push({
    text: "Continue the opening sequence",
    type: "general"
  });
  
  // Return a random question
  return questions[Math.floor(Math.random() * questions.length)];
}

/**
 * Decide whether to show hint or question
 * @param {number} threshold - Probability of showing question (0-1)
 * @returns {boolean} True if should show question, false for hint
 */
export function shouldShowQuestion(threshold = 0.5) {
  return Math.random() < threshold;
}